import { assertNever } from '@gitbook/util-ts';

import {
    REVISION_PAGE_DOCUMENT_CHANGE_ATTRIBUTES,
    type RevisionPageDocument,
    type RevisionPageDocumentChangeAttributes,
} from '../types';
import {
    comparePageCovers,
    comparePageLayouts,
    comparePageMetaLinks,
    comparePageTags,
    comparePageVariables,
} from './comparePages';

/**
 * Compute the per-attribute diff between two RevisionPageDocument snapshots.
 *
 * Returns only the keys that actually changed, excluding document body changes.
 */
export function diffRevisionPageDocumentAttributes(
    previousPage: RevisionPageDocument,
    page: RevisionPageDocument
): Omit<RevisionPageDocumentChangeAttributes, 'document'> {
    const changedAttributes: Omit<RevisionPageDocumentChangeAttributes, 'document'> = {};

    REVISION_PAGE_DOCUMENT_CHANGE_ATTRIBUTES.forEach((attribute) => {
        switch (attribute) {
            case 'title':
                if (page.title !== previousPage.title) {
                    changedAttributes.title = {
                        before: previousPage.title,
                        after: page.title,
                    };
                }
                break;
            case 'linkTitle':
                if (page.linkTitle !== previousPage.linkTitle) {
                    changedAttributes.linkTitle = {
                        before: previousPage.linkTitle,
                        after: page.linkTitle,
                    };
                }
                break;
            case 'description':
                if (page.description !== previousPage.description) {
                    changedAttributes.description = {
                        before: previousPage.description,
                        after: page.description,
                    };
                }
                break;
            case 'tagTitle':
                if (page.tagTitle !== previousPage.tagTitle) {
                    changedAttributes.tagTitle = {
                        before: previousPage.tagTitle,
                        after: page.tagTitle,
                    };
                }
                break;
            case 'slug':
                if (page.slug !== previousPage.slug) {
                    changedAttributes.slug = {
                        before: previousPage.slug,
                        after: page.slug,
                    };
                }
                break;
            case 'cover':
                if (comparePageCovers(page.cover, previousPage.cover)) {
                    changedAttributes.cover = {
                        before: previousPage.cover,
                        after: page.cover,
                    };
                }
                break;
            case 'emoji':
                if (page.emoji !== previousPage.emoji) {
                    changedAttributes.emoji = {
                        before: previousPage.emoji,
                        after: page.emoji,
                    };
                }
                break;
            case 'icon':
                if (page.icon !== previousPage.icon) {
                    changedAttributes.icon = {
                        before: previousPage.icon,
                        after: page.icon,
                    };
                }
                break;
            case 'layout':
                if (!comparePageLayouts(page.layout, previousPage.layout)) {
                    changedAttributes.layout = {
                        before: previousPage.layout,
                        after: page.layout,
                    };
                }
                break;
            case 'hidden':
                if (Boolean(page.hidden) !== Boolean(previousPage.hidden)) {
                    changedAttributes.hidden = {
                        before: Boolean(previousPage.hidden),
                        after: Boolean(page.hidden),
                    };
                }
                break;
            case 'noIndex':
                if (Boolean(page.noIndex) !== Boolean(previousPage.noIndex)) {
                    changedAttributes.noIndex = {
                        before: Boolean(previousPage.noIndex),
                        after: Boolean(page.noIndex),
                    };
                }
                break;
            case 'noRobotsIndex':
                if (Boolean(page.noRobotsIndex) !== Boolean(previousPage.noRobotsIndex)) {
                    changedAttributes.noRobotsIndex = {
                        before: Boolean(previousPage.noRobotsIndex),
                        after: Boolean(page.noRobotsIndex),
                    };
                }
                break;
            case 'condition':
                if (page.condition !== previousPage.condition) {
                    changedAttributes.condition = {
                        before: previousPage.condition,
                        after: page.condition,
                    };
                }
                break;
            case 'variables':
                if (comparePageVariables(page.variables, previousPage.variables)) {
                    changedAttributes.variables = {
                        before: previousPage.variables ?? {},
                        after: page.variables ?? {},
                    };
                }
                break;
            case 'metaLinks':
                if (comparePageMetaLinks(page.metaLinks, previousPage.metaLinks)) {
                    changedAttributes.metaLinks = {
                        before: previousPage.metaLinks ?? {},
                        after: page.metaLinks ?? {},
                    };
                }
                break;
            case 'tags':
                if (comparePageTags(page.tags, previousPage.tags)) {
                    changedAttributes.tags = {
                        before: previousPage.tags ?? [],
                        after: page.tags ?? [],
                    };
                }
                break;
            default:
                assertNever(attribute);
        }
    });

    return changedAttributes;
}
